// Operator audit log viewer. Reads audit_log via /api/admin/audit-log.
// Filters on action / resource_type / actor. Auto-refreshes every 30s.

import { useEffect, useMemo, useState } from 'react';
import { adminApi, type AuditEntry } from '../lib/api';
import { PageHeader } from '../components/Page';
import { T } from '../theme';

function timeSince(ts: string): string {
  const now = Date.now();
  const t = new Date(ts.replace(' ', 'T') + (ts.endsWith('Z') ? '' : 'Z')).getTime();
  if (Number.isNaN(t)) return ts;
  const sec = Math.max(0, Math.floor((now - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function actionColor(action: string): string {
  if (action.startsWith('brand.approve') || action.includes('connect')) return T.live;
  if (action.includes('reject') || action.includes('disconnect')) return T.error;
  if (action.includes('update') || action.includes('brief')) return T.warning;
  return T.inkSoft;
}

export default function Audit() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [filter, setFilter] = useState<{ action: string; resource_type: string; actor: string }>(
    { action: '', resource_type: '', actor: '' }
  );
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    adminApi
      .auditLog({
        action: filter.action,
        resource_type: filter.resource_type,
        actor: filter.actor,
        limit: 100,
      })
      .then((d) => setEntries(d.entries))
      .catch((e) => setError(String(e)));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.action, filter.resource_type, filter.actor]);

  const actionOptions = useMemo(() => {
    if (!entries) return [];
    return [...new Set(entries.map((e) => e.action))].sort();
  }, [entries]);

  const resourceOptions = useMemo(() => {
    if (!entries) return [];
    return [...new Set(entries.map((e) => e.resource_type))].sort();
  }, [entries]);

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle="Every state-changing operator, brand, and creator action lands here. Filter by action, resource, or actor."
      />

      {error && (
        <div style={{ padding: 12, color: T.error, fontSize: 13, background: T.bgCard, borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr) auto',
          gap: 12,
          marginBottom: 20,
          alignItems: 'end',
        }}
      >
        <FilterSelect
          label="Action"
          value={filter.action}
          options={actionOptions}
          onChange={(v) => setFilter({ ...filter, action: v })}
        />
        <FilterSelect
          label="Resource type"
          value={filter.resource_type}
          options={resourceOptions}
          onChange={(v) => setFilter({ ...filter, resource_type: v })}
        />
        <FilterText
          label="Actor"
          value={filter.actor}
          placeholder="email or contact_id"
          onChange={(v) => setFilter({ ...filter, actor: v })}
        />
        <button
          onClick={() => setFilter({ action: '', resource_type: '', actor: '' })}
          style={{
            padding: '8px 14px',
            fontSize: 11,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            background: 'transparent',
            color: T.inkSoft,
            border: `1px solid ${T.rule}`,
            borderRadius: 4,
          }}
        >
          Clear
        </button>
      </div>

      {entries === null ? (
        <div style={{ padding: 32, color: T.inkMute, fontStyle: 'normal' }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div
          style={{
            padding: 48,
            background: T.bgCard,
            border: `1px dashed ${T.rule}`,
            borderRadius: 8,
            textAlign: 'center',
            color: T.inkMute,
            fontStyle: 'normal',
          }}
        >
          No audit entries match.
        </div>
      ) : (
        <div style={{ background: T.bgCard, border: `1px solid ${T.rule}`, borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: T.bg }}>
                <Th>When</Th>
                <Th>Actor</Th>
                <Th>Action</Th>
                <Th>Resource</Th>
                <Th>Detail</Th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} style={{ borderTop: `1px solid ${T.rule}` }}>
                  <Td>
                    <span title={e.created_at}>{timeSince(e.created_at)}</span>
                  </Td>
                  <Td>
                    <span style={{ fontFamily: T.font.mono, fontSize: 12, color: T.inkSoft }}>
                      {e.actor_contact_id ?? '—'}
                    </span>
                  </Td>
                  <Td>
                    <span
                      style={{
                        fontSize: 10,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color: actionColor(e.action),
                        fontWeight: 500,
                      }}
                    >
                      {e.action}
                    </span>
                  </Td>
                  <Td>
                    <span style={{ fontFamily: T.font.mono, fontSize: 12 }}>
                      {e.resource_type}
                      {e.resource_id && (
                        <span style={{ color: T.inkSoft }}> · {e.resource_id.slice(0, 16)}{e.resource_id.length > 16 ? '…' : ''}</span>
                      )}
                    </span>
                  </Td>
                  <Td>
                    <MetaCell metadata={e.metadata} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '12px 14px',
        fontSize: 10,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: T.inkMute,
        fontWeight: 500,
        borderBottom: `1px solid ${T.rule}`,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td style={{ padding: '12px 14px', color: T.ink, verticalAlign: 'top' }}>{children}</td>
  );
}

function MetaCell({ metadata }: { metadata: string | null }) {
  if (!metadata) return <span style={{ color: T.inkMute }}>—</span>;
  let parsed: Record<string, unknown> | null = null;
  try {
    parsed = JSON.parse(metadata);
  } catch {
    return <span style={{ fontFamily: T.font.mono, fontSize: 11, color: T.inkSoft }}>{metadata.slice(0, 60)}</span>;
  }
  if (!parsed) return <span style={{ color: T.inkMute }}>—</span>;
  const entries = Object.entries(parsed).slice(0, 4);
  return (
    <span style={{ fontFamily: T.font.mono, fontSize: 11, color: T.inkSoft }}>
      {entries.map(([k, v], i) => (
        <span key={k}>
          {i > 0 && ' · '}
          <span style={{ color: T.inkMute }}>{k}=</span>
          <span style={{ color: T.ink }}>
            {typeof v === 'string' && v.length > 24 ? `${v.slice(0, 24)}…` : String(v ?? '')}
          </span>
        </span>
      ))}
    </span>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.inkMute }}>
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: '8px 10px',
          background: T.bgSoft,
          color: T.ink,
          border: `1px solid ${T.rule}`,
          borderRadius: 4,
          fontSize: 13,
        }}
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function FilterText({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.inkMute }}>
        {label}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: '8px 10px',
          background: T.bgSoft,
          color: T.ink,
          border: `1px solid ${T.rule}`,
          borderRadius: 4,
          fontSize: 13,
        }}
      />
    </div>
  );
}
